import { useAuth } from '@/hooks/useAuth';
import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { leadsService, AiDebugLog } from '@/services/leads.service';
import { Bot, MessageSquare, ChevronRight, X, ExternalLink, Sparkles, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/**
 * AI Conversation Review — admin tool to browse what the WhatsApp AI bot
 * actually saw + said for every reply.
 *
 * Solves the "flying blind" problem: previously the AiDebugLog collection
 * was being written for every bot reply but had no UI. Now operators can
 * audit prompts, see why the model misbehaved, and iterate the system
 * instructions with feedback instead of guesswork.
 */
export default function AiConversationReview() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [data, setData] = useState<{ items: AiDebugLog[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AiDebugLog | null>(null);

  useEffect(() => {
    if (!isAdmin) { setData(null); setSelected(null); setLoading(false); return; }
    setLoading(true);
    leadsService
      .listAiDebug({ page, limit: 25 })
      .then(setData)
      .catch(error => { if (!error?.response) toast.error('Failed to load AI debug logs'); })
      .finally(() => setLoading(false));
  }, [page, isAdmin]);

  if (!isAdmin) return <Card className="m-6 text-sm text-text-muted">AI conversation review is available to company administrators.</Card>;

  return (
    <>
      <Topbar
        title="AI Conversation Review"
        breadcrumb="Leads / AI"
        description="Review retained AI reply diagnostics, prompts and tool results. Access is restricted to company administrators."
      />

      <div className="px-7 py-6 grid grid-cols-1 lg:grid-cols-12 gap-4 w-full">
        {/* List column */}
        <div className={clsx('lg:col-span-5', selected && 'hidden lg:block')}>
          <Card padded={false}>
            <SectionHeader
              title="Recent bot replies"
              icon={<Bot size={16} />}
              className="px-5 pt-4 mb-2"
            />

            {loading && !data && (
              <div className="space-y-2 px-3 pb-3">
                {[1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
              </div>
            )}

            {data && data.items.length === 0 && (
              <div className="px-5 py-12 text-center text-sm text-text-muted">
                No AI bot replies yet. Set a WhatsApp account to AI chat mode and have someone message it — replies will appear here.
              </div>
            )}

            {data && data.items.length > 0 && (
              <>
                <div className="divide-y divide-border">
                  {data.items.map((log) => (
                    <button
                      key={log.debugLogUid}
                      type="button"
                      onClick={() => setSelected(log)}
                      className={clsx(
                        'w-full px-5 py-3 text-left transition-colors hover:bg-surface-alt/60',
                        selected?.debugLogUid === log.debugLogUid && 'bg-primary-soft/30',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 text-2xs text-text-muted mb-0.5">
                            <span className="font-mono">{log.model ?? '?'}</span>
                            <span>·</span>
                            <span>{log.tokensIn}↓ {log.tokensOut}↑ tokens</span>
                            {log.firstReplyToolCalls.length > 0 && (
                              <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-0.5 text-info">
                                  <Wrench size={9} /> {log.firstReplyToolCalls.length}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="line-clamp-2 text-sm text-text-primary">
                            {log.finalReplyText ?? log.firstReplyText ?? '(no reply text)'}
                          </div>
                          <div className="mt-1 text-2xs text-text-muted">
                            {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                            {' · '}
                            <Link
                              to={`/leads/${log.leadUid}`}
                              onClick={(e) => e.stopPropagation()}
                              className="underline hover:text-primary"
                            >
                              Open lead
                            </Link>
                          </div>
                        </div>
                        <ChevronRight size={14} className="shrink-0 text-text-muted mt-0.5" />
                      </div>
                    </button>
                  ))}
                </div>

                <div className="px-3 pt-2 pb-3">
                  <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Detail column */}
        <div className={clsx('lg:col-span-7', !selected && 'hidden lg:block')}>
          {!selected ? (
            <Card className="flex h-full min-h-[400px] items-center justify-center">
              <div className="text-center">
                <Sparkles size={28} className="mx-auto text-text-muted mb-2" />
                <p className="text-sm text-text-muted">Click a reply on the left to inspect it.</p>
              </div>
            </Card>
          ) : (
            <DebugDetailCard log={selected} onClose={() => setSelected(null)} />
          )}
        </div>
      </div>
    </>
  );
}

// ─── Detail card ────────────────────────────────────────────────────────────

function DebugDetailCard({ log, onClose }: { log: AiDebugLog; onClose: () => void }) {
  return (
    <Card padded={false} className="max-h-[calc(100vh-160px)] overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-surface/95 px-5 py-3 backdrop-blur">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-text-primary">AI Reply Debug</div>
          <div className="text-2xs text-text-muted font-mono truncate">{log.debugLogUid}</div>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/leads/${log.leadUid}`}>
            <Button size="sm" variant="ghost" iconLeft={<ExternalLink size={12} />}>Lead</Button>
          </Link>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary lg:hidden"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="space-y-5 p-5">
        {/* Final reply */}
        <Section title="Final reply sent to customer">
          <div className="rounded-xl border border-success/30 bg-success/5 p-3 text-sm text-text-primary whitespace-pre-wrap">
            {log.finalReplyText ?? '(empty)'}
          </div>
        </Section>

        {/* Provider stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Provider" value={log.provider ?? '—'} />
          <Stat label="Model" value={log.model ?? '—'} mono />
          <Stat label="Tokens in" value={log.tokensIn.toLocaleString()} />
          <Stat label="Tokens out" value={log.tokensOut.toLocaleString()} />
        </div>

        {/* First reply (before tools) */}
        {log.firstReplyText && log.firstReplyText !== log.finalReplyText && (
          <Section title="First reply (before tool round-trip)" subtle>
            <div className="rounded-xl border border-border bg-surface-alt/40 p-3 text-sm text-text-secondary whitespace-pre-wrap">
              {log.firstReplyText}
            </div>
          </Section>
        )}

        {/* Tool calls */}
        {log.firstReplyToolCalls.length > 0 && (
          <Section title={`Tool calls (${log.firstReplyToolCalls.length})`}>
            <div className="space-y-2">
              {log.firstReplyToolCalls.map((tc, idx) => {
                const result = log.toolResults[idx];
                return (
                  <div key={idx} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench size={12} className="text-info" />
                      <span className="font-mono text-xs font-semibold text-text-primary">{tc.name}</span>
                      {result && (
                        <span className={clsx(
                          'rounded-full px-1.5 py-0.5 text-2xs font-medium',
                          result.result.ok ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger',
                        )}>
                          {result.result.ok ? 'ok' : 'rejected'}
                        </span>
                      )}
                    </div>
                    <pre className="font-mono text-2xs text-text-secondary overflow-x-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(tc.arguments, null, 2)}
                    </pre>
                    {result && (
                      <div className="mt-1.5 border-t border-border pt-1.5 text-2xs text-text-muted">
                        → {result.result.message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Transcript passed to model */}
        <Section title={`Transcript fed to model (${log.transcript.length} messages)`}>
          <div className="space-y-1 max-h-80 overflow-y-auto rounded-xl border border-border bg-surface-alt/30 p-2">
            {log.transcript.map((m, idx) => (
              <div key={idx} className="rounded-lg px-2 py-1 text-xs">
                <div className="font-mono text-2xs text-text-muted">{m.role}{m.toolCallId ? ` (tool ${m.toolCallId.slice(0, 8)}…)` : ''}</div>
                <div className="text-text-primary whitespace-pre-wrap">{m.content ?? '(no content)'}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Tools offered */}
        {log.toolsOffered.length > 0 && (
          <Section title={`Tools offered to model (${log.toolsOffered.length})`} subtle>
            <div className="flex flex-wrap gap-1.5">
              {log.toolsOffered.map((t) => (
                <span key={t.name} className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-mono text-text-secondary">
                  {t.name}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* System prompt — the big one */}
        <Section title="System prompt (full)">
          <div className="rounded-xl border border-border bg-surface-alt/30 p-3">
            <details>
              <summary className="cursor-pointer text-xs text-text-muted hover:text-primary">
                Show full prompt ({log.systemPrompt.length.toLocaleString()} chars)
              </summary>
              <pre className="mt-2 max-h-96 overflow-y-auto font-mono text-2xs text-text-secondary whitespace-pre-wrap break-words">
                {log.systemPrompt}
              </pre>
            </details>
          </div>
        </Section>
      </div>
    </Card>
  );
}

function Section({ title, subtle, children }: { title: string; subtle?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className={clsx('mb-1.5 text-2xs font-semibold uppercase tracking-[0.14em]', subtle ? 'text-text-muted' : 'text-text-secondary')}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-surface-alt/40 px-3 py-2">
      <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
      <div className={clsx('mt-0.5 text-sm font-semibold text-text-primary', mono && 'font-mono')}>{value}</div>
    </div>
  );
}
